// ─────────────────────────────────────────────────────────────────────
// AMERICANO RESULT HELPERS
//
// Pure result/adapter helpers for the Americano tournament family, lifted
// verbatim out of legacy/App.jsx (PR-AS) as part of decommissioning the
// monolith. These were previously threaded into the views as props; they
// now live in the engine layer and are imported directly.
//
// Engine binding note: in legacy/App.jsx these called
// `Padel24Engine.elo.getPlayerKMultiplier` / `Padel24Engine.elo.applyUpdate`.
// In src/engine/platformEngine.ts those members are STATIC references to the
// `./elo` exports (`getPlayerKMultiplier`, `applyEloUpdate`) — not the
// runtime getters (only K_FACTOR / NEWBIE_* are getters reading
// EngineSettings). So importing the primitives directly is behaviour-identical.
// ─────────────────────────────────────────────────────────────────────
import { getPlayerKMultiplier, applyEloUpdate } from './elo.js';
import { resolveEffects, type EngineEffects } from './effects.js';

// Americano match result applied to the tournament state (pure function, ELO via engine).
// Shared between the Americano view and the banepad hub. The freeze modal (first_set/bingo)
// is handled separately in the view and is not part of this pure function.
export function applyAmericanoResult(
  tournament: any,
  matchId: any,
  s1: any,
  s2: any,
  allPlayers: any = [],
  effects?: EngineEffects,
) {
  // One clock read for the whole application (#203). Omitting `effects` reads
  // Date.now(), which is what this did before.
  const { now, settings } = resolveEffects(effects);
  const match = (tournament.matches || []).find((m: any) => m.id === matchId);
  if (!match) return tournament;
  const byId = (id: any) =>
    (tournament.players || []).find((p: any) => p.id === id) ||
    (allPlayers || []).find((p: any) => p.id === id);
  const p1 = byId(match.team1.p1Id),
    p2 = byId(match.team1.p2Id),
    p3 = byId(match.team2.p1Id),
    p4 = byId(match.team2.p2Id);
  if (!p1 || !p2 || !p3 || !p4) return tournament;
  const team1Slots = {
    team1: {
      p1: { kind: 'concrete', playerId: match.team1.p1Id },
      p2: { kind: 'concrete', playerId: match.team1.p2Id },
    },
    team2: {
      p1: { kind: 'concrete', playerId: match.team2.p1Id },
      p2: { kind: 'concrete', playerId: match.team2.p2Id },
    },
  };
  const playerEloPre = { [p1.id]: p1.elo, [p2.id]: p2.elo, [p3.id]: p3.elo, [p4.id]: p4.elo };
  const teamElo1Pre = Math.round((p1.elo + p2.elo) / 2);
  const teamElo2Pre = Math.round((p3.elo + p4.elo) / 2);
  const kMul = tournament.config?.kMultiplier ?? 0.5;
  const t1NewbieMul = Math.max(
    getPlayerKMultiplier(p1, now, kMul, settings),
    getPlayerKMultiplier(p2, now, kMul, settings),
  );
  const t2NewbieMul = Math.max(
    getPlayerKMultiplier(p3, now, kMul, settings),
    getPlayerKMultiplier(p4, now, kMul, settings),
  );
  // `now` is passed down so the multipliers RECORDED on the match are the ones
  // actually applied to the ELO. applyEloUpdate has always accepted it; this
  // call simply never supplied it, so it read its own clock and also stamped
  // `lastMatchAt` from it.
  const newPlayers = applyEloUpdate(tournament.players, team1Slots, s1, s2, kMul, now, settings);
  const newP1 = newPlayers.find((p: any) => p.id === p1.id),
    newP3 = newPlayers.find((p: any) => p.id === p3.id);
  const eloChange1 = newP1 ? newP1.elo - p1.elo : 0;
  const eloChange2 = newP3 ? newP3.elo - p3.elo : 0;
  const updatedMatches = tournament.matches.map((m: any) =>
    m.id === matchId
      ? {
          ...m,
          score1: s1,
          score2: s2,
          completed: true,
          eloChange1,
          eloChange2,
          teamElo1Pre,
          teamElo2Pre,
          playerEloPre,
          t1NewbieMul,
          t2NewbieMul,
          playedAt: m.playedAt || now,
        }
      : m,
  );
  return { ...tournament, matches: updatedMatches, players: newPlayers };
}

// Adapter: Americano match (team1.p1Id/p2Id) → Padel24 «slot» form that CourtPadCard/
// MatchupLines expect. The score fields (score1/score2/completed) are already identical.
export function americanoMatchToSlots(m: any) {
  const toSlot = (id: any) => ({ kind: 'concrete', playerId: id });
  const conv = (team: any) =>
    !team || (team.p1 && team.p1.kind)
      ? team
      : { ...team, p1: toSlot(team.p1Id), p2: toSlot(team.p2Id) };
  return { ...m, team1: conv(m.team1), team2: conv(m.team2) };
}
